import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaTrophy, FaBolt, FaTachometerAlt } from 'react-icons/fa';
import * as FaIcons from 'react-icons/fa';
import confetti from 'canvas-confetti';
import Button from '../components/common/Button';
import GameBackground from '../components/game/GameBackground';
import SEOHead from '../components/SEO/SEOHead';

const Results = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isReady, setIsReady] = useState(false);

  const resultData = location.state || {};
  const {
    score = 0,
    level = 1,
    mode = 'timeChallenge',
    stats = {},
    unlockedAchievements = []
  } = resultData;

  const safeStats = {
    totalPopped: stats?.totalPopped || 0,
    maxCombo: stats?.maxCombo || 0
  };

  useEffect(() => {
    console.log('Results page received state:', resultData);

    if (!location.state || score === 0) {
      console.warn('No valid game data, redirecting to home');
      navigate('/', { replace: true });
      return;
    }

    setIsReady(true);

    const timer1 = setTimeout(() => {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }, 100);

    if (unlockedAchievements.length > 0) {
      const timer2 = setTimeout(() => {
        confetti({
          particleCount: 150,
          spread: 100,
          origin: { y: 0.5 },
          colors: ['#FFD700', '#FFA500', '#FF6347']
        });
      }, 600);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }

    return () => {
      clearTimeout(timer1);
    };
  }, [location.state, navigate, score, unlockedAchievements]);

  const getGradeEmoji = () => {
    if (score >= 2000) return '🏆';
    if (score >= 1500) return '🥇';
    if (score >= 1000) return '🥈';
    if (score >= 500) return '🥉';
    return '⭐';
  };

  const getGradeText = () => {
    if (score >= 2000) return '傳說級';
    if (score >= 1500) return '大師級';
    if (score >= 1000) return '專家級';
    if (score >= 500) return '高手級';
    return '新手級';
  };

  const getSpeedText = () => {
    if (level >= 5) return '光速';
    if (level >= 4) return '閃電';
    if (level >= 3) return '疾風';
    if (level >= 2) return '衝刺';
    return '起步';
  };

  const getSpeedColor = () => {
    if (level >= 5) return 'text-red-600';
    if (level >= 4) return 'text-purple-600';
    if (level >= 3) return 'text-cyan-600';
    if (level >= 2) return 'text-green-600';
    return 'text-blue-600';
  };

  if (!isReady) {
    return (
      <>
        <GameBackground />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-2xl font-bold text-gray-600 animate-pulse">
            載入結果中...
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`遊戲結束 - 得分 ${score} - 泡泡樂園`}
        description={`恭喜獲得${getGradeText()}！最終得分：${score}，等級：${level}，最高連擊：${safeStats.maxCombo}x`}
      />

      <GameBackground />

      <main className="min-h-screen flex items-center justify-center p-4">
        <article className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 sm:p-8 shadow-2xl max-w-lg w-full">
          <header>
            <h1 className="text-3xl sm:text-4xl font-bold text-center mb-6 bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
              遊戲結束!
            </h1>
          </header>

          <section className="bg-gradient-to-br from-yellow-400 to-orange-500 text-white rounded-2xl p-4 mb-4 text-center">
            <div className="text-5xl sm:text-6xl mb-2" role="img" aria-label={getGradeText()}>
              {getGradeEmoji()}
            </div>
            <div className="text-xl sm:text-2xl font-bold">{getGradeText()}</div>
          </section>

          <section className="bg-gradient-to-br from-pink-500 to-purple-500 text-white rounded-2xl p-5 mb-4 text-center">
            <div className="text-xs sm:text-sm uppercase tracking-wider mb-2 opacity-90">
              最終得分
            </div>
            <div className="text-5xl sm:text-6xl font-bold mb-3" aria-label={`${score}分`}>
              {score}
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm mt-4">
              <div className="bg-white/20 rounded-xl p-2">
                <div className="opacity-90 text-xs mb-1">等級</div>
                <div className="text-2xl font-bold">{level}</div>
              </div>
              <div className="bg-white/20 rounded-xl p-2">
                <div className="opacity-90 text-xs mb-1">點擊數</div>
                <div className="text-2xl font-bold">{safeStats.totalPopped}</div>
              </div>
            </div>
          </section>

          <section className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-purple-100 rounded-xl p-3 text-center">
              <FaBolt className="text-purple-600 text-xl mx-auto mb-1" aria-hidden="true" />
              <div className="text-[10px] text-gray-600">最高連擊</div>
              <div className="text-xl font-bold text-purple-600">
                {safeStats.maxCombo}x
              </div>
            </div>
            <div className="bg-blue-100 rounded-xl p-3 text-center">
              <FaTachometerAlt className={`${getSpeedColor()} text-xl mx-auto mb-1`} aria-hidden="true" />
              <div className="text-[10px] text-gray-600">速度等級</div>
              <div className={`text-xl font-bold ${getSpeedColor()}`}>{getSpeedText()}</div>
            </div>
          </section>

          {unlockedAchievements.length > 0 && (
            <section className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 mb-4">
              <h2 className="flex items-center justify-center gap-2 text-lg font-bold text-yellow-700 mb-3">
                <FaTrophy aria-hidden="true" />
                解鎖新成就!
              </h2>
              <ul className="space-y-2 max-h-40 overflow-y-auto">
                {unlockedAchievements.map(achievement => {
                  const Icon = FaIcons[achievement.icon];
                  return (
                    <li
                      key={achievement.id}
                      className="flex items-center gap-2 bg-white rounded-lg p-2"
                    >
                      <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center text-white flex-shrink-0">
                        {Icon && <Icon size={16} aria-hidden="true" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm text-gray-800 truncate">
                          {achievement.name}
                        </div>
                        <div className="text-xs text-gray-600 truncate">
                          {achievement.description}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <nav className="flex flex-col gap-3">
            <Button
              onClick={() => navigate(`/play?mode=${mode}`)}
              variant="primary"
              className="w-full"
              aria-label="再玩一次"
            >
              🔄 再玩一次
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="secondary"
              className="w-full"
              aria-label="回到主選單"
            >
              🏠 回主選單
            </Button>
          </nav>
        </article>
      </main>
    </>
  );
};

export default Results;